import { cardBlocksToHtml } from "@shared/lib/card-stack/unified-migration"
import { mergeAttributes, Node } from "@tiptap/core"
import type { Node as PMNode } from "@tiptap/pm/model"
import type { EditorState } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { JoinListDOMSerializer } from "tiptap-extension-flat-list"
import { CommentContentComponent } from "./comment-content-component"

/** An empty line, which is what Enter leaves behind at the end of a comment. */
const isBlankLine = (node: PMNode) => node.type.name === "paragraph" && node.content.size === 0

/** What the comment says. The blank lines trailing it are not words. */
function commentBlocks(state: EditorState): PMNode[] {
  const content = state.doc.firstChild
  if (!content || content.type.name !== "commentContent") return []

  const blocks: PMNode[] = []
  content.content.forEach((block) => blocks.push(block))
  while (blocks.length > 0 && isBlankLine(blocks[blocks.length - 1])) blocks.pop()
  return blocks
}

/** Whether the comment says nothing — what a submit button disables on. */
export function commentIsEmpty(state: EditorState): boolean {
  return commentBlocks(state).length === 0
}

/**
 * What the comment says, as the HTML a card comment stores (`commentText` — see CommentItem),
 * or "" when it says nothing. The commentContent wrapper is the editor's, not the comment's, so
 * only what the user typed inside it is serialized.
 */
export function readCommentHtml(state: EditorState): string {
  const blocks = commentBlocks(state)
  if (blocks.length === 0) return ""
  return cardBlocksToHtml(blocks, JoinListDOMSerializer.fromSchema(state.schema, "getHTML"))
}

/**
 * The body of a card comment: the blocks the user types, wearing the one Add button that
 * fills them.
 */
export const ExtensionCommentContent = Node.create({
  name: "commentContent",
  group: "blockContainer addTarget",
  content: "block+",
  draggable: false,

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-content-type": "commentContent" }), 0]
  },

  parseHTML() {
    return [{ tag: 'div[data-content-type="commentContent"]' }]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CommentContentComponent)
  },
})
